/* eslint-disable max-len */

import { Box, Divider, Grid, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Download } from "lucide-react";
import { useMemo, useState } from "react";
import ProgressBar from "react-customizable-progressbar";
import { useTranslation } from "react-i18next";
import type { CampaignDto, CampaignScoresDto } from "types";
import {
  // CardCampaignsBox,
  CardCampaignsItemsBox, InfoTooltip,
  StyledPercentageItem
} from "./CardUserCampaigns.styles";
import { useCampaignScoresQuery, useCampaignsQuery, useDownloadCampaignCertificates } from "../../queries/campaigns";
import { useAuthStore } from "../../stores";
import { downloadBlobFile } from "../../utils/general";
import { ButtonLink } from "../Button";
import { CommonWidgetContainer } from "../Commons/CommonWidgetContainer.styles";
import { Spinner } from "../Spinner";

const MAX_LEN = 4;

export function CardUserCampaigns({ enabled = true }: { enabled?: boolean } = {}) {
  const theme = useTheme();
  const [showAll, setShowAll] = useState(false);
  const campaignsEnabled = useAuthStore(state => state.corporateInfo?.collect_campaigns);
  const { t } = useTranslation();
  const { data:scores, isLoading: scoresIsLoading } = useCampaignScoresQuery({ enabled: campaignsEnabled && enabled });
  const scoresMap = (scores ?? []).reduce((acc: { [key: number]: CampaignScoresDto }, item) => {
    acc[item.campaign_id] = item;
    return acc;
  },
    {} as { [key: number]: CampaignScoresDto });
  const { mutateAsync: mutateCertificate, isLoading: certificateIsLoading } = useDownloadCampaignCertificates();
  const { data } = useCampaignsQuery({ enabled: campaignsEnabled && enabled });
  const campaignsWithScores: (CampaignDto & {
    credits: number,
    hours: number,
    points: number,
    label: string,
    score: number
  })[] = useMemo(() => {
    const campaigns = data?.output ?? [];
    const scoreLabelMap: {
      [key in "HOURS" | "CREDITS" | "POINTS"]: {
        field: keyof Pick<CampaignScoresDto, "points" | "hours" | "credits">,
        label: string
      }
    } = {
      "HOURS": {
        field: "hours",
        label: "campaigns_hours"
      },
      "CREDITS": {
        field: "credits",
        label: "campaigns_credits"
      },
      "POINTS": {
        field: "points",
        label: "campaigns_points"
      }
    };
    return campaigns.map((campaign) => {
      const scores = scoresMap[campaign.id];
      const labels = scoreLabelMap[campaign.unit];
      return {
        ...campaign,
        credits: scores?.credits,
        hours: scores?.hours,
        points: scores?.points,
        score: scores?.[labels.field],
        label: labels.label
      };
    });
  }, [data, scoresMap]);

  const formatNumber = (value?: number): string => Number.isInteger(value) ? (value ?? 0).toString() : (value ?? 0).toFixed(2);

  const campaignsToShow = showAll ? campaignsWithScores : campaignsWithScores.slice(0, MAX_LEN);

  return (
    campaignsEnabled && campaignsWithScores.length ?
      <CommonWidgetContainer
        role="region"
        aria-label={ t("campaigns_your_training") }
        className="widget-item"
        id="user-scores-widget"
        // hasSidePadding={ false }
        // $profilePage={ isProfilePage }
      >
        <Grid container justifyContent={ "space-between" } alignSelf={ "stretch" }>
          <Grid item>
            <Typography
              tabIndex={ 0 }
              aria-label={ t("campaigns_your_training") }
              fontWeight={ 700 }
              lineHeight="1.3125rem"
              fontSize="1rem"
              color={ theme.customColors.exploreCardText }
              sx={ {
                marginRight: "10px"

                // [theme.breakpoints.down("sm")]: {
                //   fontSize: theme.spacing(1.75)
                // }

              } }
            >
              { t("campaigns_your_training") }
            </Typography>
          </Grid>
          <Grid item sx={ {
            position: "relative"
          } }>
            { !certificateIsLoading ? (
              <InfoTooltip arrow placement={ "top" } title={ (<Typography fontSize="0.85rem">
                { t("campaigns_download_certificate") }
              </Typography>) }>
                <IconButton
                  id="download-user-scores"
                  aria-label={ t("campaigns_download_certificate") }
                  style={ { padding: 0 } }
                  onClick={ async () => {
                    const certificateBlob = await mutateCertificate();
                    downloadBlobFile({
                      fileName: "campaign certificates.zip",
                      blobFile: certificateBlob
                    });
                  } }
                >
                  <Download
                    size="1.5rem"
                    stroke={ theme.customColors.systemSecondary01 }
                    aria-hidden="true"
                    focusable="false"
                  />
                </IconButton>
              </InfoTooltip>
            ) : <Spinner width={ "24px" } height={ "24px" } color={ "secondary" } size={ "24px" } padding={ "0" }></Spinner> }
          </Grid>
        </Grid>

        { scoresIsLoading ? (
          <Box sx={ { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 120 } }>
            <Spinner size={ 24 } padding={ 0 } />
          </Box>
        ) : (
          <>
            <Grid aria-hidden={ "true" } container alignItems={ "center" } alignSelf={ "stretch" } justifyContent={ "space-between" }>
              <Grid item xs={ 5 }>
                <Typography
                  fontWeight={ 500 }
                  fontSize="0.75rem"
                  color={ theme.customColors.systemSecondary01 }
                >
                  { t("campaigns_campaign") }
                </Typography>
              </Grid>
              <Grid item xs={ 5 }>
                <Typography
                  fontWeight={ 500 }
                  fontSize="0.75rem"
                  color={ theme.customColors.systemSecondary01 }
                >
                  { t("campaigns_unit") }
                </Typography>
              </Grid>
              <Grid item xs={ 2 }>
                <Typography
                  fontWeight={ 500 }
                  fontSize="0.75rem"
                  color={ theme.customColors.systemSecondary01 }
                >
                  { t("campaigns_status") }
                </Typography>
              </Grid>
              <Typography
                fontWeight={ 500 }
                fontSize="0.75rem"
                color={ theme.customColors.systemSecondary01 }
              >
                { t("campaigns_status") }
              </Typography>
            </Grid>
            <CardCampaignsItemsBox showAll={ showAll }>
              {
                campaignsToShow.map((campaign) => <>
                  <Grid
                    tabIndex={ 0 }
                    aria-label={ `${campaign.name}" "${Math.round(((campaign.score > campaign.target ? campaign.target : campaign.score) / campaign.target) * 100)} % ` + t("completed") }
                    container alignItems={ "center" }
                    alignSelf={ "stretch" }
                    justifyContent={ "space-between" }
                    sx={ {
                      "&:hover, &:focus-visible": {
                        // backgroundColor: theme.customColors.backgroundSecondaryHover //TODO ripristinare
                        backgroundColor:`rgba(${theme.customColors.backgroundSecondaryHover})`
                      },
                      borderRadius: "0.5rem",
                      padding: "0.5rem"
                    } }
                  >
                    <Grid aria-hidden={ "true" } item xs={ 5 }>
                      <Typography fontSize={ theme.spacing(1.5) }>{ campaign.name }</Typography>
                    </Grid>
                    <Grid aria-hidden={ "true" } item xs={ 5 }>
                      <Typography
                        fontSize={ theme.spacing(1.5) }>{ formatNumber(campaign.score ?? 0.0) }/{ formatNumber(campaign.target) } { t(campaign.label) }</Typography>
                    </Grid>
                    <Grid aria-hidden={ "true" } item xs={ 2 }>
                      <StyledPercentageItem key={ `campaign-${campaign.id}` }>
                        <InfoTooltip arrow placement={ "top" } title={ (
                          <Typography fontSize="0.85rem">
                            { `${Math.round(((campaign.score > campaign.target? campaign.target : campaign.score)/campaign.target)*100)} % ` }{ t("completed") }
                          </Typography>
                        ) }>
                          <Box style={ { alignSelf: "flex-end" } }>
                            <ProgressBar
                              // className={ item.classname }
                              radius={ 26 }
                              progress={ campaign.score > campaign.target? campaign.target : campaign.score }
                              steps={ campaign.target }
                              // cut={ item.cut }
                              strokeColor={ theme.customColors.systemSecondary01 }
                              pointerStrokeWidth={ 0 }
                              strokeWidth={ 8 }
                              trackStrokeWidth={ 8 }
                              // rotate={ item.rotate }
                              trackStrokeColor={ theme.customColors.border }
                              // initialAnimationDelay={ 3000 }
                              // initialAnimation={ true }
                              trackTransition=".1s ease"
                              transition="1s ease"
                            />
                          </Box>
                        </InfoTooltip>
                      </StyledPercentageItem>
                    </Grid>
                  </Grid>
                  <Divider
                    color={ theme.customColors.border }

                    sx={ {
                      backgroundColor: theme.customColors.border,
                      borderRadius: "50rem",
                      height: "0.125rem",
                      margin: "0.5rem 0rem"
                    } }
                  ></Divider>
                </>)
              }
            </CardCampaignsItemsBox>
            { campaignsWithScores.length > MAX_LEN? <ButtonLink
              disableRipple
              onClick={ () => {
                setShowAll(old => !old);
              } }
              sx={ {
                cursor:"pointer",
                fontWeight:"700",
                padding:0,
                textDecoration:"underline",
                zIndex:"2",
                "&:hover": {
                  background:"none",
                  boxShadow:"none"
                },
                fontSize:"0.75rem",
                color: theme.palette.secondary?.main
              } }
            >
              { showAll? t("close") : t("view_all") }
            </ButtonLink> : <></> }
          </>
        ) }
      </CommonWidgetContainer> : <></>
  );
}
// TODO inserire bottone
// <ButtonDigited typology="secondary" buttonSize="sm" selfPosition="flex-end">
//   <Link href="/agenda"
//     aria-label={ t("go_to_campaigns", "Vedi campagne") }
//   >
//     { /* TODO TRADUZIONE */ }
//     { t("go_to_campaigns", "Vedi campagne") + " " } <ChevronRight size="0.875rem" />
//   </Link>
// </ButtonDigited>
